<div class="page-head">
  <div>
    <h1>{{ pageTitle }}</h1>
    <div class="sub">
      @if (!isLoading) { {{ filteredUsers.length }} shown — {{ pageSubtitle }} } @else { Loading… }
    </div>
  </div>
  @if (canCreateUser && !isLoading) {
    <div class="head-actions">
      <ng-container *staffCan="'Users.Create'"><button type="button" class="act" (click)="startAddUser()">＋ Add user</button></ng-container>
    </div>
  }
</div>

@if (!isLoading) {
  <div class="page-body">
    <div class="split">
      <div class="list-pane">
        <input
          class="inp"
          type="text"
          placeholder="Search users…"
          [(ngModel)]="searchTerm"
          (ngModelChange)="onSearch($event)" />

        <!-- Platform staff must sign in with an authenticator app; only the Plus page has any. -->
        @if (userType === 'plus') {
          <div class="list-filters" role="group" aria-label="Filter">
            <button type="button" class="act-ghost act-sm" [class.on]="staffFilter === 'all'"
              (click)="setStaffFilter('all')">All</button>
            <button type="button" class="act-ghost act-sm" [class.on]="staffFilter === 'staff'"
              (click)="setStaffFilter('staff')">Platform staff</button>
            <button type="button" class="act-ghost act-sm" [class.on]="staffFilter === 'notSetUp'"
              (click)="setStaffFilter('notSetUp')">Not set up · {{ staffNotSetUpCount }}</button>
          </div>
          @if (staffFilter !== 'all') {
            <div class="staff-count" role="status">
              {{ staffCount }} staff · {{ staffNotSetUpCount }} not set up
              @if (staffNotSetUpCount) {
                <span class="muted">— asked to set up an authenticator at their next sign-in; nothing for an admin to do.</span>
              }
            </div>
          }
        }

        <ul class="rows">
          @for (user of filteredUsers; track user.userId) {
            <li class="row clickable"
              [class.active]="selectedUser?.userId === user.userId">
              <a class="row-link" [routerLink]="[baseRoute, user.userId]" (click)="onSelectUser(user)">
                <div class="row-title">
                  {{ user.firstName }} {{ user.lastName }}
                  <span class="pill pill-sm account-badge">{{ accountLabel(user) }}</span>
                  @if (user.isDisabled) {
                    <span class="pill pill-sm pill-red account-badge" title="Sign-in switched off">Disabled</span>
                  }
                </div>
                <div class="row-sub">{{ user.email }}</div>
              </a>
              @if (userType === 'plus') {
                <span class="row-side authenticator-cell" title="Authenticator">
                  @if (userRequiresAuthenticator(user)) {
                    @if (authenticatorOn(user)) {
                      <span class="pill pill-sm pill-green">On</span>
                    } @else {
                      <span class="pill pill-sm pill-amber">Not set up</span>
                    }
                  } @else {
                    —
                  }
                </span>
              }
            </li>
          }
          @if (!filteredUsers.length) {
            <li class="empty">No users match.</li>
          }
        </ul>
      </div>

      <!-- RIGHT: user details -->
      <div class="detail-pane">
        @if (createdTempPassword) {
          <div class="note note-warn temp-password-note" role="alert">
            <div>
              <strong>Temporary password for {{ createdUserEmail }}:</strong>
              <code class="mono">{{ createdTempPassword }}</code>
            </div>
            <p>Share it with the user now — it will not be shown again. They should change it after signing in.</p>
            <button type="button" class="act-link" (click)="dismissTempPassword()">Dismiss</button>
          </div>
        }

        @if (showAddUser) {
          <div class="panel">
            <div class="panel-title">Add user</div>
            @if (addUserError) { <div class="note note-err">{{ addUserError }}</div> }

            <ng-container *staffCan="'Users.Create'"><form [formGroup]="addUserForm" (ngSubmit)="submitAddUser()">
              <div class="form-grid">
                <label class="field">
                  <span>First name *</span>
                  <input class="inp" type="text" formControlName="firstName" />
                </label>
                <label class="field">
                  <span>Last name *</span>
                  <input class="inp" type="text" formControlName="lastName" />
                </label>
                <label class="field">
                  <span>Email *</span>
                  <input class="inp" type="email" formControlName="email" />
                </label>
                <label class="field">
                  <span>Phone</span>
                  <input class="inp" type="text" formControlName="phoneNumber" />
                </label>
                <label class="field">
                  <span>System role</span>
                  <select class="inp" formControlName="roleName">
                    <option [ngValue]="null">— None —</option>
                    @for (r of systemRoleOptions; track r.roleId) {
                      <option [ngValue]="r.roleName">{{ r.displayName || r.roleName }}</option>
                    }
                  </select>
                </label>
              </div>

              <p class="hint">A temporary password is generated and shown once after creation.</p>

              <div class="form-actions">
                <button type="button" class="act-ghost" (click)="cancelAddUser()" [disabled]="addUserSaving">Cancel</button>
                <button type="submit" class="act" [disabled]="addUserSaving">
                  {{ addUserSaving ? 'Creating…' : 'Create user' }}
                </button>
              </div>
            </form></ng-container>
          </div>
        } @else if (selectedUser) {
          <div class="panel selected-user-header">
            <div class="who">
              <h2>
                {{ selectedUser.firstName }} {{ selectedUser.lastName }}
                @if (selectedUser.isDisabled) {
                  <span class="pill pill-sm pill-red">Disabled</span>
                }
                @if (userRequiresAuthenticator(selectedUser)) {
                  @if (authenticatorOn(selectedUser)) {
                    <span class="pill pill-sm pill-green" title="Authenticator app set up">Authenticator on</span>
                  } @else {
                    <span class="pill pill-sm pill-amber" title="Asked to set up an authenticator at their next sign-in">Authenticator not set up</span>
                  }
                }
              </h2>
              <div class="row-sub">{{ selectedUser.email }}</div>
            </div>
            <div class="head-actions">
              <a class="act-ghost act-sm" [routerLink]="['/profile', selectedUser.userId]">View profile</a>
              <fieldset class="edit-scope inline" [disabled]="!editing">
                @if (canChangeAccountStatus) {
                  @if (selectedUser.isDisabled) {
                    <button type="button" class="act-ghost act-sm"
                      [disabled]="accountStatusSaving" (click)="enableSelectedAccount()">
                      {{ accountStatusSaving ? 'Enabling…' : 'Enable account' }}
                    </button>
                  } @else {
                    <button type="button" class="act-danger act-sm"
                      [disabled]="accountStatusSaving" (click)="requestDisableAccount()">Disable account</button>
                  }
                }
                <ng-container *staffCan="'Users.Delete'"><button type="button" class="act-danger act-sm"
                  [disabled]="deletingUser || !canChangeSelectedUser" (click)="requestDeleteUser()">Delete user</button></ng-container>
              </fieldset>
            </div>
          </div>

          @if (selectedUser.isDisabled) {
            <div class="note note-err" role="status">
              Disabled{{ selectedUser.disabledAtUtc ? ' on ' + (selectedUser.disabledAtUtc | date: 'd MMM y, HH:mm') : '' }}{{ selectedUser.disabledReason ? ': ' + selectedUser.disabledReason : '' }}.
              They can't sign in or use a POS PIN, and the POS, back office and marketplace refuse
              anything they still had open. Each till withdraws their offline PIN the next time it is online.
              @if (canChangeAccountStatus) { Switch to edit mode and choose <strong>Enable account</strong> to restore it. }
            </div>
          }
          @if (accountStatusWarning) {
            <div class="note note-warn" role="status">{{ accountStatusWarning }}</div>
          }

          @if (confirmDisableAccount) {
            <fieldset class="edit-scope" [disabled]="!editing">
              <div class="note note-err delete-confirm" role="alertdialog">
                <span>
                  Disable <strong>{{ selectedUser.firstName }} {{ selectedUser.lastName }}</strong>
                  ({{ selectedUser.email }})? They can't sign in or use a POS PIN until the account is
                  enabled again. The POS, back office and marketplace refuse anything they still have open
                  within a minute, and each till withdraws their offline PIN the next time it is online.
                  Messaging, the AI assistant and finance keep a session that is already open until it expires.
                </span>
                <label class="field disable-reason">
                  <span>Reason (kept in the audit trail)</span>
                  <input class="inp" type="text" maxlength="500" [(ngModel)]="disableReason"
                    placeholder="e.g. Left the company, chargeback fraud" />
                </label>
                <div class="head-actions">
                  <button type="button" class="act-danger act-sm" [disabled]="accountStatusSaving"
                    (click)="confirmDisableSelectedAccount()">
                    {{ accountStatusSaving ? 'Disabling…' : 'Yes, disable' }}
                  </button>
                  <button type="button" class="act-link" [disabled]="accountStatusSaving" (click)="cancelDisableAccount()">Cancel</button>
                </div>
              </div>
            </fieldset>
          }
          @if (accountStatusError) {
            <div class="note note-err">{{ accountStatusError }}</div>
          }

          <!-- View-only until Edit: only one admin can change a user at a time. -->
          <app-edit-lock-bar
            class="edit-lock"
            entityType="user"
            noun="user"
            [entityId]="selectedUser.userId"
            (editingChange)="onEditingChange($event)">
          </app-edit-lock-bar>

          @if (confirmDeleteUser) {
            <div class="note note-err delete-confirm" role="alertdialog">
              <span>
                Delete <strong>{{ selectedUser.firstName }} {{ selectedUser.lastName }}</strong>
                ({{ selectedUser.email }})? This cannot be undone.
              </span>
              <label>Reason <textarea class="inp" [(ngModel)]="deleteReason" required maxlength="2000"></textarea></label>
              <div class="head-actions">
                <ng-container *staffCan="'Users.Delete'"><button type="button" class="act-danger act-sm" [disabled]="deletingUser || !deleteReason.trim()" (click)="confirmDeleteSelectedUser()">
                  {{ deletingUser ? 'Deleting…' : 'Yes, delete' }}
                </button></ng-container>
                <button type="button" class="act-link" [disabled]="deletingUser" (click)="cancelDeleteUser()">Cancel</button>
              </div>
            </div>
          }
          @if (deleteUserError) {
            <div class="note note-err">{{ deleteUserError }}</div>
          }

          <!-- Issues -->
          <section class="panel">
            <div class="panel-title">Profile issues</div>
            @if (userProfileIssues.length > 0) {
              <ul class="issue-list">
                @for (issue of userProfileIssues; track issue) {
                  <li>
                    <span class="alert-badge">{{ issue }}</span>
                    @if (issue.includes('system role')) {
                      <a href="#system-roles" class="act-link">Assign system role</a>
                    }
                    @if (issue.includes('No businesses')) {
                      <a href="#businesses" class="act-link">Add business</a>
                    }
                    @if (issue.includes('Missing first') || issue.includes('Missing last')) {
                      <a [routerLink]="['/profile', selectedUser!.userId]" class="act-link">Edit profile</a>
                    }
                  </li>
                }
              </ul>
            } @else {
              <span class="muted">No issues detected for this profile.</span>
            }
          </section>

          <!-- Subscriptions: every plan record, any status. Also explains why a
               user with history cannot be deleted. -->
          <section class="panel" id="subscriptions">
           <fieldset class="edit-scope" [disabled]="!editing">
            <div class="panel-head">
              <div class="panel-title" style="margin:0">Subscriptions</div>
              @if (canManagePlansForSelectedUser && !showPlanForm) {
                <div class="head-actions">
                  <ng-container *staffCan="'Users.Plans.Manage'"><button type="button" class="act-ghost act-sm" (click)="startChangePlan()">Change plan</button></ng-container>
                  @if (activeUserPlan) {
                    <ng-container *staffCan="'Users.Plans.Manage'"><button type="button" class="act-danger act-sm"
                      [disabled]="planSaving" (click)="requestCancelPlan()">Disable</button></ng-container>
                  }
                </div>
              }
            </div>

            @if (planActionMessage) { <div class="note note-ok">{{ planActionMessage }}</div> }
            @if (planActionError) { <div class="note note-err">{{ planActionError }}</div> }

            @if (activeUserPlanCount > 1) {
              <div class="note note-err">
                <strong>This user has {{ activeUserPlanCount }} active plans.</strong>
                A user should only ever hold one. Keep the row marked
                <em>Live subscription</em> — that is the one the payment provider is charging —
                and use <em>Cancel this</em> on the others. If more than one is live, or none are,
                check the provider before cancelling: cancelling here does not stop billing.
              </div>
            }

            @if (confirmCancelPlan) {
              <div class="note note-warn plan-confirm" role="alertdialog">
                <div>
                  Disable <strong>{{ activeUserPlan?.planName }}</strong> for
                  {{ selectedUser.firstName }} {{ selectedUser.lastName }}? Access is revoked
                  immediately. <strong>This does not cancel billing</strong> at Stripe, Paystack
                  or Momo — that must be cancelled separately or the card keeps being charged.
                </div>
                <div class="head-actions">
                  <button type="button" class="act-link" [disabled]="planSaving" (click)="dismissCancelPlan()">Cancel</button>
                  <ng-container *staffCan="'Users.Plans.Manage'"><button type="button" class="act-danger act-sm" [disabled]="planSaving" (click)="confirmDisablePlan()">
                    {{ planSaving ? 'Disabling…' : 'Yes, disable' }}
                  </button></ng-container>
                </div>
              </div>
            }

            @if (showPlanForm) {
              <ng-container *staffCan="'Users.Plans.Manage'"><form class="plan-form" [formGroup]="planForm" (ngSubmit)="submitChangePlan()">
                <div class="note note-warn">
                  This changes the plan record only. No subscription is created or amended at the
                  payment provider, so an existing one keeps billing at its old amount.
                </div>

                @if (!selectedUserBusinesses.length) {
                  <div class="note note-err">
                    This user isn’t linked to a business yet. A plan holder owns a business, so
                    assign one under <strong>Businesses</strong> below before changing the plan.
                  </div>
                }

                <div class="form-grid">
                  <label class="field">
                    <span>Plan *</span>
                    <select class="inp" formControlName="planId">
                      <option [ngValue]="null" disabled>Select a plan…</option>
                      @for (p of plans; track p.planID) {
                        <option [ngValue]="p.planID">{{ p.planName }}</option>
                      }
                    </select>
                  </label>

                  <label class="field">
                    <span>Business *</span>
                    <select class="inp" formControlName="businessId">
                      <option [ngValue]="null" disabled>Select the business…</option>
                      @for (b of selectedUserBusinesses; track b.businessId) {
                        <option [ngValue]="b.businessId">{{ b.name }}</option>
                      }
                    </select>
                    <small class="hint">
                      They become <strong>BusinessOwner</strong> of this business. Only businesses
                      they’re already linked to are listed — taking over someone else’s business
                      isn’t allowed; the owner invites them as co-owner instead.
                    </small>
                  </label>

                  <label class="field">
                    <span>Mode</span>
                    <select class="inp" formControlName="mode">
                      <option value="active">Paid override</option>
                      <option value="trial">Trial</option>
                    </select>
                  </label>

                  @if (planForm.value.mode === 'trial') {
                    <label class="field">
                      <span>Trial ends</span>
                      <input class="inp" type="date" formControlName="trialEndsAt" />
                      <small class="hint">Leave blank for the default trial length.</small>
                    </label>
                    <label class="field">
                      <span>At trial end</span>
                      <select class="inp" formControlName="conversionMode">
                        <option value="manual">Convert manually</option>
                        <option value="auto">Convert automatically</option>
                      </select>
                    </label>
                  } @else {
                    <label class="field">
                      <span>Billing cycle</span>
                      <select class="inp" formControlName="billingCycle">
                        <option value="Monthly">Monthly</option>
                        <option value="Yearly">Yearly</option>
                      </select>
                    </label>
                  }

                  <label class="field">
                    <span>Country</span>
                    <select class="inp" formControlName="countryCode">
                      <option [ngValue]="null">— Not set —</option>
                      @for (c of countries; track c.countryId) {
                        <option [ngValue]="c.countryISO || c.countryCode">{{ c.countryName }}</option>
                      }
                    </select>
                    <small class="hint">Resolves pricing and the processor shown after saving.</small>
                  </label>
                </div>

                <p class="hint role-note">
                  Holding a plan grants the system role <strong>{{ planOwnerRole }}</strong>,
                  replacing their current one, plus <strong>BusinessOwner</strong> on the selected business.
                </p>

                <div class="form-actions">
                  <button type="button" class="act-ghost" (click)="cancelChangePlan()" [disabled]="planSaving">Cancel</button>
                  <button type="submit" class="act" [disabled]="planSaving || !selectedUserBusinesses.length">
                    {{ planSaving ? 'Saving…' : 'Change plan' }}
                  </button>
                </div>
              </form></ng-container>
            }

            @if (plansLoading) {
              <span class="muted">Loading subscriptions…</span>
            } @else if (plansError) {
              <div class="note note-err">{{ plansError }}</div>
            } @else if (!userPlans.length) {
              <span class="muted">
                @if (selectedUserIsCustomerOnly) {
                  This is a marketplace customer account — it buys on the marketplace and
                  holds no Plus subscription.
                } @else {
                  No subscriptions for this user.
                }
              </span>
            } @else {
              <div class="tbl-wrap">
                <table class="tbl">
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Cycle</th>
                      <th>Provider billing</th>
                      <th>Start</th>
                      <th>End</th>
                      @if (canManagePlans) { <th class="actions"></th> }
                    </tr>
                  </thead>
                  <tbody>
                    @for (plan of userPlans; track plan.userPlanId) {
                      <tr>
                        <td class="row-title">{{ plan.planName }}</td>
                        <td>
                          <span class="pill"
                            [class.pill-green]="planStatusClass(plan) === 'ok'"
                            [class.pill-grey]="planStatusClass(plan) === 'inactive'">
                            {{ planStatusLabel(plan) }}
                          </span>
                        </td>
                        <td>{{ plan.billingCycle || '—' }}</td>
                        <td>
                          @if (plan.subscriptionId) {
                            <span class="pill pill-sm pill-amber" [title]="plan.subscriptionId">Live subscription</span>
                          } @else {
                            <span class="muted">None</span>
                          }
                        </td>
                        <td>{{ plan.startDate ? (plan.startDate | date: 'mediumDate') : '—' }}</td>
                        <td>{{ plan.endDate ? (plan.endDate | date: 'mediumDate') : '—' }}</td>
                        @if (canManagePlans) {
                          <td class="actions">
                            @if (plan.isActive) {
                              @if (confirmCancelPlanId === plan.userPlanId) {
                                <button type="button" class="act-link" [disabled]="planSaving" (click)="dismissCancelPlanRow()">Keep</button>
                                <ng-container *staffCan="'Users.Plans.Manage'"><button type="button" class="act-link danger" style="margin-left:10px"
                                  [disabled]="planSaving" (click)="confirmCancelPlanRow()">
                                  {{ planSaving ? 'Cancelling…' : 'Confirm cancel' }}
                                </button></ng-container>
                              } @else {
                                <ng-container *staffCan="'Users.Plans.Manage'"><button type="button" class="act-link danger"
                                  [disabled]="planSaving" (click)="requestCancelPlanRow(plan)">Cancel this</button></ng-container>
                              }
                            }
                          </td>
                        }
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
           </fieldset>
          </section>

          <!-- System roles -->
          <section class="panel" id="system-roles">
           <fieldset class="edit-scope" [disabled]="!editing">
            <div class="panel-title">System roles</div>

            @if (selectedUserSystemRoles.length > 0) {
              <div class="roles-list">
                @for (role of selectedUserSystemRoles; track role.roleId) {
                  <span class="role-pill">
                    {{ role.displayName || role.roleName }}
                    @if (role.isSystemLevel === false) {
                      <span class="alert-badge" title="Role marked non-system; required fix">required fix</span>
                    }
                    <button
                      type="button"
                      class="pill-remove"
                      [disabled]="roleSaving || !canManageSelectedRole(role.roleName)" (click)="removeSystemRole(role.roleName)"
                      aria-label="Remove system role">
                      ×
                    </button>
                  </span>
                }
              </div>
            } @else {
              <span class="muted">No system roles assigned.</span>
            }

            <div class="role-add">
              <label class="field">
                <span>Add system role</span>
                <select class="inp" [disabled]="roleSaving || !canChangeSelectedUser || !availableRolesForSelectedUser.length" (change)="onAddRoleChange($event)">
                  <option value="" selected disabled>Select role…</option>
                  @for (r of availableRolesForSelectedUser; track r.roleId) {
                    <option [value]="r.roleName">{{ r.displayName || r.roleName }}</option>
                  }
                </select>
              </label>
            </div>

            @if (pendingRoleName) {
              <section class="note note-warn" role="alertdialog" aria-label="Confirm role grant">
                <p>Grant <strong>{{ pendingRoleName }}</strong> to {{ selectedUser.email }}?</p>
                <p>Current staff rank: {{ selectedStaffRank ?? 'Non-staff' }}. Your rank: {{ staffAuth.staffRank }}.</p>
                <p *ngIf="roleError" role="alert">{{ roleError }}</p>
                <button type="button" class="act" (click)="confirmAddRole()" [disabled]="roleSaving">Confirm grant</button>
                <button type="button" class="act-ghost" (click)="cancelAddRole()" [disabled]="roleSaving">Cancel</button>
              </section>
            }
            @if (confirmRemoveSystemRoleName) {
              <div class="modal-backdrop"></div>
              <div class="modal">
                <div class="modal-header">Confirm removal</div>
                <div class="modal-body">
                  Remove system role <strong>{{ confirmRemoveSystemRoleName }}</strong> from this user?
                  <label>Reason <textarea class="inp" [(ngModel)]="roleRevokeReason" required maxlength="2000"></textarea></label>
                </div>
                <div class="modal-actions">
                  <button type="button" class="act-ghost" (click)="cancelRemoveSystemRole()">Cancel</button>
                  <button type="button" class="act" (click)="confirmRemoveSystemRole()" [disabled]="roleSaving || !roleRevokeReason.trim()">Remove</button>
                </div>
              </div>
            }
           </fieldset>
          </section>

          <!-- System permissions -->
          <section class="panel">
            <div class="panel-title">System permissions</div>

            @if (permissionsError) {
              <div class="note note-err">{{ permissionsError }}</div>
            } @else if (selectedUserSystemPermissions.length > 0) {
              <div class="perm-list">
                @for (p of selectedUserSystemPermissions; track p.code) {
                  <span class="perm-pill mono">{{ p.code }}</span>
                }
              </div>
            } @else {
              <span class="muted">No system permissions assigned.</span>
            }
          </section>

          <!-- Businesses + business roles & permissions -->
          <section class="panel" id="businesses">
            <div class="panel-title">Businesses</div>

            <!-- Marketplace customers own no business, so their page offers no assignment. -->
            @if (selectedUser && canAssignBusinesses) {
             <fieldset class="edit-scope" [disabled]="!editing">
              <div class="role-add">
                <label class="field">
                  <span>Assign to business</span>
                  <select class="inp" [(ngModel)]="newBusinessId" (ngModelChange)="assignBusinessError = ''">
                    <option value="" selected disabled>Select business…</option>
                    @for (b of assignableBusinesses; track b.businessId) {
                      <option [value]="b.businessId">{{ businessOptionLabel(b) }}</option>
                    }
                  </select>
                </label>
                <label class="field field-narrow">
                  <span>As</span>
                  <select class="inp" [(ngModel)]="assignRoleName" (ngModelChange)="assignBusinessError = ''">
                    @for (r of assignRoleOptions; track r.roleName) {
                      <option [value]="r.roleName">{{ r.label }}</option>
                    }
                  </select>
                </label>
                <ng-container *staffCan="'Businesses.Membership.Manage'"><button
                  type="button"
                  class="act"
                  (click)="addUserToBusiness(newBusinessId)"
                  [disabled]="!newBusinessId || !canAssignSelectedUser || !!assignOwnerConflict || assignOwnerNotHolder || !assignRoleIsOffered || assigningBusiness">
                  {{ assigningBusiness ? 'Assigning…' : 'Assign' }}
                </button></ng-container>
              </div>
             </fieldset>
              @if (assignOwnerNotHolder) {
                <div class="note note-warn assign-note">
                  Only an account holder can own a business. Grant {{ selectedUser.firstName }} AccountHolder (Plus)
                  or MarketplaceAccountHolder (Marketplace) under System roles first, or assign them as staff.
                </div>
              }
              @if (assignOwnerConflict) {
                <div class="note note-warn assign-note">
                  {{ assignOwnerConflict }} already owns this business. Pick one without an owner, or add them as staff.
                </div>
              }
              @if (assignBusinessError) {
                <div class="note note-err assign-note">{{ assignBusinessError }}</div>
              }
            }

            @if (selectedUserBusinesses.length > 0) {
              <app-user-businesses
                [businesses]="selectedUserBusinesses"
                [selectedBusiness]="selectedBusiness"
                (selectBusiness)="onSelectBusiness($event)"
              ></app-user-businesses>

              @if (selectedBusiness) {
               <fieldset class="edit-scope" [disabled]="!editing">
                <div class="role-add">
                  <label class="field">
                    <span>Add business role</span>
                    <ng-container *staffCan="'Businesses.Membership.Manage'"><select class="inp" [(ngModel)]="newBusinessRoleName" (change)="onBusinessRoleSelect($event)">
                      <option value="" selected disabled>Select role…</option>
                      @for (r of availableBusinessRoles; track r.roleName) {
                        <option [value]="r.roleName">{{ r.displayName }}</option>
                      }
                    </select></ng-container>
                  </label>
                  <ng-container *staffCan="'Businesses.Membership.Manage'"><button type="button" class="act" (click)="addBusinessRole(newBusinessRoleName)" [disabled]="!newBusinessRoleName">Add</button></ng-container>
                </div>

                @if (selectedBusinessRoles.length > 0) {
                  <div class="roles-list">
                    @for (role of selectedBusinessRoles; track role.roleId) {
                      <span class="role-pill">
                        {{ role.displayName || role.roleName }}
                        <ng-container *staffCan="'Businesses.Membership.Manage'"><button
                          type="button"
                          class="pill-remove"
                          (click)="removeBusinessRole(role.roleName)"
                          aria-label="Remove business role">
                          ×
                        </button></ng-container>
                      </span>
                    }
                  </div>
                } @else {
                  <span class="muted">No roles assigned in this business.</span>
                }
               </fieldset>
              }

              <app-business-permissions
                [business]="{ businessId: selectedBusiness?.businessId!, name: selectedBusiness?.name, roles: selectedBusinessRoles, permissions: selectedBusinessPermissions }"
              ></app-business-permissions>

              @if (confirmRemoveRoleName) {
                <div class="modal-backdrop"></div>
                <fieldset class="edit-scope" [disabled]="!editing">
                <div class="modal">
                  <div class="modal-header">Confirm removal</div>
                  <div class="modal-body">
                    Remove role <strong>{{ confirmRemoveRoleName }}</strong> from this business?
                  </div>
                  <div class="modal-actions">
                    <button type="button" class="act-ghost" (click)="cancelRemoveBusinessRole()">Cancel</button>
                    <ng-container *staffCan="'Businesses.Membership.Manage'"><button type="button" class="act" (click)="confirmRemoveBusinessRole()">Remove</button></ng-container>
                  </div>
                </div>
                </fieldset>
              }
            } @else {
              <span class="muted">No businesses assigned to this user.</span>
            }
          </section>

          @if (toast) {
            <div class="toast">{{ toast }}</div>
          }

        } @else {
          <div class="panel empty">Select a user to manage roles and permissions.</div>
        }
      </div>
    </div>
  </div>
} @else {
  <div class="loading">Loading users…</div>
}
